import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { isHiddenCodeBlockContent } from '../utils';

function extractMarkdownText(node: React.ReactNode): string {
  if (typeof node === 'string' || typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map(extractMarkdownText).join('');
  if (React.isValidElement<{ children?: React.ReactNode }>(node)) {
    return extractMarkdownText(node.props.children);
  }
  return '';
}

function cleanOrphanedMarkdown(content: string): string {
  let text = content;
  const boldMarkers = text.match(/\*\*/g);
  if (boldMarkers && boldMarkers.length % 2 !== 0) {
    text = text.replace(/^\s*\*\*\s*/, '');
    text = text.replace(/\s*\*\*\s*$/, '');
  }
  return text.trim();
}

interface MessageMarkdownProps {
  content: string;
  isUser?: boolean;
}

const MessageMarkdown: React.FC<MessageMarkdownProps> = ({ content, isUser = false }) => {
  const markdown = cleanOrphanedMarkdown(content);
  if (!markdown) return null;

  return (
    <div className="max-w-none font-family-messina-sans text-gray-800 [&>*:last-child]:mb-0 [&>*:first-child]:mt-0">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          h3: ({ children }) => (
            <h3 className="mt-4 mb-2 text-base font-semibold text-gray-900">{children}</h3>
          ),
          h4: ({ children }) => (
            <h4 className="mt-3 mb-1.5 text-sm font-semibold text-gray-900">{children}</h4>
          ),
          p: ({ children }) => (
            <p
              className={`text-sm text-gray-800 ${
                isUser ? 'mb-1 leading-normal last:mb-0' : 'mb-3 leading-relaxed last:mb-0'
              }`}
            >
              {children}
            </p>
          ),
          ul: ({ children }) => <ul className="my-2 ml-5 list-disc space-y-1">{children}</ul>,
          ol: ({ children }) => <ol className="my-2 ml-5 list-decimal space-y-1">{children}</ol>,
          li: ({ children }) => <li className="text-sm leading-relaxed">{children}</li>,
          strong: ({ children }) => <strong className="font-semibold text-gray-900">{children}</strong>,
          pre: ({ children }) => {
            if (isHiddenCodeBlockContent(extractMarkdownText(children))) return null;
            return (
              <pre className="overflow-x-auto rounded-lg bg-gray-900 text-gray-100 border border-gray-200 p-3 text-xs my-3">
                {children}
              </pre>
            );
          },
          code: ({ className, children, ...props }) => {
            const isInline = !className;
            if (!isInline && isHiddenCodeBlockContent(extractMarkdownText(children))) {
              return null;
            }
            return isInline ? (
              <code
                className="rounded bg-gray-100 px-1.5 py-0.5 text-xs font-mono text-gray-800"
                {...props}
              >
                {children}
              </code>
            ) : (
              <code className={className} {...props}>
                {children}
              </code>
            );
          },
          a: ({ href, children }) => (
            <a
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 hover:underline underline-offset-2"
            >
              {children}
            </a>
          ),
          table: ({ children }) => (
            <div className="my-3 overflow-x-auto rounded-lg border border-gray-200">
              <table className="w-full text-xs">{children}</table>
            </div>
          ),
          th: ({ children }) => (
            <th className="border-b border-gray-200 bg-gray-50 px-3 py-2 text-left font-semibold text-gray-900">
              {children}
            </th>
          ),
          td: ({ children }) => (
            <td className="border-b border-gray-100 px-3 py-2 text-gray-800">{children}</td>
          ),
          blockquote: ({ children }) => (
            <blockquote className="border-l-4 border-gray-300 pl-3 my-2 text-gray-600 italic">
              {children}
            </blockquote>
          ),
        }}
      >
        {markdown}
      </ReactMarkdown>
    </div>
  );
};

export default MessageMarkdown;
